import React, { useState, useEffect } from 'react';
import { X, Sun, Moon, Sunset, Heart, Sparkles, Leaf, NotebookPen } from 'lucide-react';
import { Button, Card, Input, MoodSelector, type Mood } from '../ui';
import { dailyCheckInService } from '../../services';
import './CheckInReminderModal.css';

// Warm, encouraging quotes
const quotes = [
  { text: "Every day is a new canvas.", author: "Unknown", emoji: "🎨" },
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain", emoji: "🌱" },
  { text: "Creativity takes courage.", author: "Henri Matisse", emoji: "💜" },
  { text: "Start where you are. Use what you have. Do what you can.", author: "Arthur Ashe", emoji: "✨" },
  { text: "You don't have to be perfect to be wonderful.", author: "Unknown", emoji: "🌸" },
];

// Gentle prompts for intentions
const intentionPrompts = [
  "Maybe: Practice that tricky part...",
  "Maybe: Mix the chorus section...",
  "Maybe: Just open the project...",
  "Maybe: Sketch one new idea...",
  "Maybe: Review yesterday's work...",
];

interface CheckInReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckInComplete: (mood: Mood, intention?: string) => void;
  userName?: string;
}

export const CheckInReminderModal: React.FC<CheckInReminderModalProps> = ({
  isOpen,
  onClose,
  onCheckInComplete,
  userName = 'Creator',
}) => {
  const [selectedMood, setSelectedMood] = useState<Mood | undefined>();
  const [intention, setIntention] = useState('');
  const [didMorningPages, setDidMorningPages] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quote, setQuote] = useState(quotes[0]);
  const [intentionPlaceholder, setIntentionPlaceholder] = useState('');

  useEffect(() => {
    if (isOpen) {
      setQuote(quotes[Math.floor(Math.random() * quotes.length)]);
      setIntentionPlaceholder(intentionPrompts[Math.floor(Math.random() * intentionPrompts.length)]);
    }
  }, [isOpen]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return {
      text: 'Good morning',
      Icon: Sun,
      subtext: 'A new day, a fresh start.',
      emoji: '🌅'
    };
    if (hour < 18) return {
      text: 'Good afternoon',
      Icon: Sunset,
      subtext: 'Still time to create something.',
      emoji: '🌤️'
    };
    return {
      text: 'Good evening',
      Icon: Moon,
      subtext: 'The quiet hours can be magical.',
      emoji: '🌙'
    };
  };

  const { text: greetingText, subtext, emoji: greetingEmoji } = getGreeting();

  const getMoodMessage = (mood: Mood) => {
    const messages: Record<Mood, { emoji: string; text: string }> = {
      inspired: { emoji: '✨', text: "What a wonderful feeling! Let that inspiration guide you." },
      energized: { emoji: '⚡', text: "Feeling the energy! Great days make great progress." },
      okay: { emoji: '🌤️', text: "Okay days count too. You're here, that's what matters." },
      tired: { emoji: '🫖', text: "It's okay to feel tired. Rest is part of the process." },
      blocked: { emoji: '🌫️', text: "Take a deep breath. Creative blocks happen to everyone." },
    };
    return messages[mood];
  };

  const handleSubmit = async () => {
    if (!selectedMood) return;
    setIsSubmitting(true);

    try {
      await dailyCheckInService.createCheckIn({
        mood: selectedMood,
        notes: undefined,
        dailyIntention: intention || undefined,
      });
      onCheckInComplete(selectedMood, intention || undefined);
      onClose();
    } catch (error) {
      console.error('Failed to create check-in:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="checkin-modal-overlay">
      <div className="checkin-modal" onClick={(e) => e.stopPropagation()}>
        <button className="checkin-modal__close" onClick={onClose}>
          <X size={20} />
        </button>

        {/* Header with greeting */}
        <div className="checkin-modal__header">
          <span className="checkin-modal__emoji">{greetingEmoji}</span>
          <h2 className="checkin-modal__greeting">{greetingText}, {userName}!</h2>
          <p className="checkin-modal__subtitle">{subtext}</p>
        </div>

        {/* Quote */}
        <Card variant="default" padding="sm" className="checkin-modal__quote">
          <span className="checkin-modal__quote-emoji">{quote.emoji}</span>
          <p className="checkin-modal__quote-text">"{quote.text}"</p>
          <span className="checkin-modal__quote-author">— {quote.author}</span>
        </Card>

        <div className="checkin-modal__content">
          {/* Mood Selection */}
          <section className="checkin-modal__section">
            <div className="checkin-modal__section-header">
              <Heart size={18} className="checkin-modal__section-icon" />
              <h3 className="checkin-modal__section-title">How are you feeling?</h3>
            </div>
            <MoodSelector value={selectedMood} onChange={setSelectedMood} size="lg" />
            {selectedMood && (
              <div className="checkin-modal__response">
                <span>{getMoodMessage(selectedMood).emoji}</span>
                <span>{getMoodMessage(selectedMood).text}</span>
              </div>
            )}
          </section>

          {/* Daily Intention */}
          <section className="checkin-modal__section">
            <div className="checkin-modal__section-header">
              <Leaf size={18} className="checkin-modal__section-icon" />
              <h3 className="checkin-modal__section-title">Set a gentle intention</h3>
            </div>
            <Input
              placeholder={intentionPlaceholder}
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              fullWidth
            />
          </section>

          {/* Morning Pages */}
          <section className="checkin-modal__section">
            <div className="checkin-modal__section-header">
              <NotebookPen size={18} className="checkin-modal__section-icon" />
              <p className="checkin-modal__section-label">Did you write morning pages today?</p>
            </div>
            <div className="checkin-modal__morning-pages">
              <button
                type="button"
                className={`checkin-modal__mp-option ${didMorningPages === true ? 'checkin-modal__mp-option--selected' : ''}`}
                onClick={() => setDidMorningPages(true)}
              >
                <span>✨</span>
                <span>Yes!</span>
              </button>
              <button
                type="button"
                className={`checkin-modal__mp-option ${didMorningPages === false ? 'checkin-modal__mp-option--selected' : ''}`}
                onClick={() => setDidMorningPages(false)}
              >
                <span>🌱</span>
                <span>Not yet</span>
              </button>
            </div>
          </section>
        </div>

        {/* Actions */}
        <div className="checkin-modal__actions">
          <Button
            fullWidth
            size="lg"
            onClick={handleSubmit}
            disabled={!selectedMood}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Begin My Day'}
          </Button>
          <button className="checkin-modal__skip" onClick={onClose}>
            I'll check in later
          </button>
        </div>

        {/* Footer */}
        <div className="checkin-modal__footer">
          <Sparkles size={14} />
          <p>Taking a moment to check in is an act of self-care.</p>
        </div>
      </div>
    </div>
  );
};

export default CheckInReminderModal;
